// src/modules/aiml/BrainSection.jsx
// Brain.js — Build, Train, Visualize neural networks in-browser (no download needed)
// Tabs: overview | build | train | xor | patterns | rnn | visualize | concepts

import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  BarChart, Bar, Cell, Legend,
} from 'recharts';

const ACCENT = '#ef4444';
import { _livePalette as P } from '../../context/paletteSync';
const TABS = ['overview','build','train','xor','patterns','rnn','visualize','concepts'];

const Btn = ({ onClick, children, color = ACCENT, disabled, style }) => (
  <button onClick={onClick} disabled={disabled} style={{ padding: '8px 18px', background: color + '18', border: `1px solid ${color}`, color, fontSize: '10px', cursor: disabled ? 'not-allowed' : 'pointer', borderRadius: '3px', fontFamily: 'JetBrains Mono', fontWeight: 700, opacity: disabled ? 0.4 : 1, ...style }}>{children}</button>
);
const Tag = ({ children, color = ACCENT }) => (
  <span style={{ padding: '2px 8px', background: color + '20', border: `1px solid ${color}44`, color, fontSize: '9px', borderRadius: '2px', fontFamily: 'JetBrains Mono', fontWeight: 700 }}>{children}</span>
);
const Panel = ({ title, accent = ACCENT, children, style }) => (
  <div style={{ background: 'var(--surface)', border: '1px solid var(--b0)', borderRadius: '4px', overflow: 'hidden', ...style }}>
    <div style={{ padding: '10px 14px', borderBottom: '1px solid var(--b0)', fontSize: '9px', letterSpacing: '.1em', color: accent, fontWeight: 700, fontFamily: 'JetBrains Mono' }}>{title}</div>
    <div style={{ padding: '14px' }}>{children}</div>
  </div>
);

// ── Brain.js CDN loader ────────────────────────────────────────────
const BRAIN_CDN = 'https://cdn.jsdelivr.net/npm/brain.js@2.0.0-beta.23/dist/brain-browser.min.js';
let _brainLoaded = false;
async function ensureBrain() {
  if (_brainLoaded && window.brain && window.brain.NeuralNetwork) return;
  await new Promise((res, rej) => {
    if (window.brain && window.brain.NeuralNetwork) { _brainLoaded = true; res(); return; }
    const s = document.createElement('script');
    s.src = BRAIN_CDN; s.onload = () => { _brainLoaded = true; res(); }; s.onerror = rej;
    document.head.appendChild(s);
  });
}

// ── Activation functions ──────────────────────────────────────────
const ACTS = {
  sigmoid: x => 1 / (1 + Math.exp(-x)),
  relu:    x => Math.max(0, x),
  tanh:    x => Math.tanh(x),
  leaky:   x => x >= 0 ? x : 0.01 * x,
};
const dSigmoid = x => { const s = ACTS.sigmoid(x); return s * (1 - s); };
const dTanh    = x => 1 - Math.tanh(x) ** 2;
const dRelu    = x => x > 0 ? 1 : 0;
const dLeaky   = x => x > 0 ? 1 : 0.01;
const DACTS    = { sigmoid: dSigmoid, tanh: dTanh, relu: dRelu, leaky: dLeaky };

// ── Pure-JS feedforward NN (no Brain.js dependency for XOR speed) ─
class TinyNN {
  constructor(hiddenSize = 4, lr = 0.5, actName = 'sigmoid') {
    const r = () => (Math.random() - 0.5) * 2;
    this.w1 = Array.from({ length: hiddenSize }, () => [r(), r()]);
    this.b1 = Array.from({ length: hiddenSize }, r);
    this.w2 = Array.from({ length: hiddenSize }, r);
    this.b2 = r();
    this.lr = lr; this.actName = actName;
    this.act = ACTS[actName] || ACTS.sigmoid;
    this.dact = DACTS[actName] || dSigmoid;
  }
  forward(inp) {
    this.inp = inp;
    this.z1 = this.w1.map((w, i) => w[0] * inp[0] + w[1] * inp[1] + this.b1[i]);
    this.h  = this.z1.map(z => this.act(z));
    this.z2 = this.w2.reduce((s, w, i) => s + w * this.h[i], this.b2);
    this.out = ACTS.sigmoid(this.z2);
    return this.out;
  }
  backward(target) {
    const err = this.out - target;
    const dOut = err * dSigmoid(this.z2);
    this.w2 = this.w2.map((w, i) => w - this.lr * dOut * this.h[i]);
    this.b2 -= this.lr * dOut;
    this.w1 = this.w1.map((row, i) => {
      const d = dOut * this.w2[i] * this.dact(this.z1[i]);
      return [row[0] - this.lr * d * this.inp[0], row[1] - this.lr * d * this.inp[1]];
    });
    this.b1 = this.b1.map((b, i) => b - this.lr * dOut * this.w2[i] * this.dact(this.z1[i]));
    return err * err;
  }
  predict(inp) { return this.forward(inp); }
  snapshot() { return { w1: this.w1.map(r => [...r]), w2: [...this.w2], b1: [...this.b1], b2: this.b2 }; }
}

// ── Decision Boundary Canvas ──────────────────────────────────────
function DecisionBoundary({ netRef, xorData }) {
  const cvs = useRef(null);
  useEffect(() => {
    const net = netRef.current;
    if (!net || !cvs.current) return;
    const canvas = cvs.current, ctx = canvas.getContext('2d');
    const W = canvas.width, H = canvas.height;
    const img = ctx.createImageData(W, H);
    for (let y = 0; y < H; y++) {
      for (let x = 0; x < W; x++) {
        const v = net.predict([x / W, y / H]);
        const i = (y * W + x) * 4;
        img.data[i]   = v > 0.5 ? 20  : 80;
        img.data[i+1] = v > 0.5 ? 80  : 20;
        img.data[i+2] = v > 0.5 ? 50  : 20;
        img.data[i+3] = 160;
      }
    }
    ctx.putImageData(img, 0, 0);
    xorData.forEach(({ input, output }) => {
      ctx.beginPath(); ctx.arc(input[0] * W, input[1] * H, 8, 0, Math.PI * 2);
      ctx.fillStyle = output[0] > 0.5 ? '#4ade80' : '#f472b6'; ctx.fill();
      ctx.strokeStyle = '#fff'; ctx.lineWidth = 2; ctx.stroke();
    });
  });
  return <canvas ref={cvs} width={200} height={200} style={{ borderRadius: '4px', border: '1px solid var(--b0)', display: 'block' }} />;
}

// ── Weight Heatmap ────────────────────────────────────────────────
function WeightHeatmap({ snap }) {
  if (!snap) return null;
  const { w1, w2 } = snap;
  const allW = [...w1.flat(), ...w2];
  const maxA = Math.max(...allW.map(Math.abs), 0.01);
  const cell = (v, key) => (
    <div key={key} title={v.toFixed(3)} style={{ width: '36px', height: '36px', display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: '3px', fontSize: '8px', fontFamily: 'JetBrains Mono', fontWeight: 700, color: '#fff',
      background: v >= 0 ? `rgba(52,211,153,${Math.min(1,Math.abs(v)/maxA*0.9+0.1)})` : `rgba(239,68,68,${Math.min(1,Math.abs(v)/maxA*0.9+0.1)})` }}>
      {v.toFixed(1)}
    </div>
  );
  return (
    <div style={{ display: 'flex', gap: '20px', flexWrap: 'wrap', alignItems: 'flex-start' }}>
      <div>
        <div style={{ fontSize: '9px', color: 'var(--t3)', marginBottom: '5px' }}>W1 — Input→Hidden ({w1.length} neurons)</div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '3px' }}>
          {w1.map((row, i) => <div key={i} style={{ display: 'flex', gap: '3px' }}>{row.map((v, j) => cell(v, j))}</div>)}
        </div>
      </div>
      <div>
        <div style={{ fontSize: '9px', color: 'var(--t3)', marginBottom: '5px' }}>W2 — Hidden→Output</div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '3px' }}>
          {w2.map((v, i) => <div key={i}>{cell(v, i)}</div>)}
        </div>
      </div>
    </div>
  );
}

// ── Network Diagram (SVG) ─────────────────────────────────────────
function NetDiagram({ layers }) {
  const W = 340, H = 190;
  const maxN = Math.max(...layers.map(l => l.n));
  const xs = layers.map((_, i) => 40 + i * ((W - 80) / Math.max(layers.length - 1, 1)));
  const nodes = layers.map((l, li) => {
    const n = Math.min(l.n, 8);
    return Array.from({ length: n }, (_, ni) => ({ x: xs[li], y: H / 2 + (ni - (n - 1) / 2) * (H / (maxN + 1)) }));
  });
  return (
    <svg width={W} height={H} style={{ overflow: 'visible' }}>
      {nodes.slice(0, -1).map((src, li) => src.map(s => nodes[li + 1].map((d, di) => (
        <line key={`${li}-${di}`} x1={s.x} y1={s.y} x2={d.x} y2={d.y} stroke="var(--b1)" strokeWidth={0.7} opacity={0.5} />
      ))))}
      {nodes.map((ln, li) => ln.map(({ x, y }, ni) => (
        <g key={`${li}-${ni}`}>
          <circle cx={x} cy={y} r={13} fill="var(--raised)" stroke={P[li % P.length]} strokeWidth={2} />
          <text x={x} y={y + 4} textAnchor="middle" fontSize={7} fontFamily="JetBrains Mono" fill={P[li % P.length]} fontWeight={700}>
            {li === 0 ? `x${ni+1}` : li === layers.length - 1 ? 'y' : `h${ni+1}`}
          </text>
        </g>
      )))}
      {layers.map((l, li) => (
        <text key={li} x={xs[li]} y={H - 2} textAnchor="middle" fontSize={8} fontFamily="JetBrains Mono" fill="var(--t3)">
          {l.label}
        </text>
      ))}
    </svg>
  );
}

// ═════════════════════════════════════════════════════════════════
export default function BrainSection({ gpuInfo }) {
  const [tab, setTab]           = useState('overview');
  const [brainReady, setBrainReady] = useState(false);

  // Build
  const [layers, setLayers]     = useState([{ n: 2, label: 'Input' }, { n: 4, label: 'Hidden 1' }, { n: 1, label: 'Output' }]);
  const [buildAct, setBuildAct] = useState('sigmoid');

  // XOR
  const [xorRunning, setXorRunning] = useState(false);
  const [xorLoss, setXorLoss]     = useState([]);
  const [xorEpochs, setXorEpochs] = useState(0);
  const [xorPreds, setXorPreds]   = useState([]);
  const [xorSnap, setXorSnap]     = useState(null);
  const [xorHidden, setXorHidden] = useState(4);
  const [xorLR, setXorLR]         = useState(0.5);
  const [xorAct, setXorAct]       = useState('sigmoid');
  const xorNetRef = useRef(null);
  const xorRAF    = useRef(null);

  const XOR = [{ input:[0,0],output:[0]},{ input:[1,0],output:[1]},{ input:[0,1],output:[1]},{ input:[1,1],output:[0]}];

  // Patterns
  const [patterns, setPatterns]   = useState([
    { input:'sunny,warm', output:'beach' },
    { input:'cold,rain',  output:'home' },
    { input:'snowy,cold', output:'ski' },
    { input:'warm,park',  output:'park' },
  ]);
  const [patStatus, setPatStatus] = useState('idle');
  const [patQuery, setPatQuery]   = useState('sunny,hot');
  const [patResult, setPatResult] = useState(null);
  const [patLoss, setPatLoss]     = useState([]);
  const patRef = useRef(null);

  // RNN
  const [rnnText, setRnnText]     = useState('hello world how are you today hello world');
  const [rnnStatus, setRnnStatus] = useState('idle');
  const [rnnSeed, setRnnSeed]     = useState('hel');
  const [rnnResult, setRnnResult] = useState('');
  const [rnnLoss, setRnnLoss]     = useState([]);
  const rnnRef = useRef(null);

  // Custom train
  const [csvData, setCsvData]     = useState('x1,x2,y\n0.1,0.2,0\n0.8,0.9,1\n0.2,0.1,0\n0.7,0.8,1\n0.15,0.25,0\n0.85,0.75,1');
  const [trainStatus, setTrainStatus] = useState('idle');
  const [trainLoss, setTrainLoss] = useState([]);
  const [trainHidden, setTrainHidden] = useState(4);
  const [trainEpochs, setTrainEpochs] = useState(500);

  useEffect(() => { ensureBrain().then(() => setBrainReady(true)).catch(() => {}); }, []);
  useEffect(() => () => cancelAnimationFrame(xorRAF.current), []);

  // ── XOR ──────────────────────────────────────────────────────────
  const startXOR = useCallback(() => {
    if (xorRunning) { setXorRunning(false); cancelAnimationFrame(xorRAF.current); return; }
    const net = new TinyNN(xorHidden, xorLR, xorAct);
    xorNetRef.current = net;
    setXorLoss([]); setXorEpochs(0); setXorRunning(true);
    let epoch = 0;
    const hist = [];
    const step = () => {
      let loss = 0;
      for (let i = 0; i < 50; i++) {
        let L = 0;
        XOR.forEach(({ input, output }) => { net.forward(input); L += net.backward(output[0]); });
        epoch++;
        if (i === 49) { hist.push({ epoch, loss: +(L / 4).toFixed(5) }); loss = L / 4; }
      }
      setXorLoss(hist.slice(-100));
      setXorEpochs(epoch);
      setXorSnap(net.snapshot());
      setXorPreds(XOR.map(({ input, output }) => ({ inp: input.join(','), exp: output[0], pred: +net.predict(input).toFixed(3) })));
      if (epoch < 5000) xorRAF.current = requestAnimationFrame(step);
      else setXorRunning(false);
    };
    xorRAF.current = requestAnimationFrame(step);
  }, [xorRunning, xorHidden, xorLR, xorAct]);

  // ── PATTERNS ─────────────────────────────────────────────────────
  const trainPatterns = async () => {
    if (!brainReady) return;
    await ensureBrain();
    setPatStatus('training'); setPatLoss([]);
    const { NeuralNetwork } = window.brain;
    const vocab   = [...new Set(patterns.flatMap(p => p.input.split(',').flatMap(s => [...s])))].sort();
    const classes = [...new Set(patterns.map(p => p.output))];
    const encInp = str => { const v = new Array(vocab.length).fill(0); str.split(',').forEach(w => [...w].forEach(c => { const i = vocab.indexOf(c); if (i >= 0) v[i] += 1 / w.length; })); return v; };
    const encOut = lbl => { const v = new Array(classes.length).fill(0); const i = classes.indexOf(lbl); if (i >= 0) v[i] = 1; return v; };
    const net = new NeuralNetwork({ hiddenLayers: [8, 8], activation: 'sigmoid', learningRate: 0.3 });
    const lh = [];
    net.train(patterns.map(p => ({ input: encInp(p.input), output: encOut(p.output) })), {
      iterations: 2000, errorThresh: 0.005, callbackPeriod: 100,
      callback: ({ iterations, error }) => { lh.push({ epoch: iterations, loss: +error.toFixed(5) }); setPatLoss([...lh]); },
    });
    patRef.current = { net, vocab, classes, encInp };
    setPatStatus('ready');
  };

  const runPattern = () => {
    if (!patRef.current) return;
    const { net, classes, encInp } = patRef.current;
    const res = net.run(encInp(patQuery));
    setPatResult(classes.map((c, i) => ({ label: c, score: res[i] || 0 })).sort((a, b) => b.score - a.score));
  };

  // ── RNN ───────────────────────────────────────────────────────────
  const trainRNN = async () => {
    if (!brainReady) return;
    await ensureBrain();
    setRnnStatus('training'); setRnnLoss([]);
    const { recurrent: { LSTM } } = window.brain;
    const net = new LSTM();
    const lh = [];
    net.train([rnnText], { iterations: 500, errorThresh: 0.01, callbackPeriod: 50,
      callback: ({ iterations, error }) => { lh.push({ epoch: iterations, loss: +error.toFixed(5) }); setRnnLoss([...lh]); },
    });
    rnnRef.current = net;
    setRnnStatus('ready');
  };

  const runRNN = () => {
    if (!rnnRef.current) return;
    try { setRnnResult(rnnRef.current.run(rnnSeed)); }
    catch { setRnnResult('(error — try a shorter seed)'); }
  };

  // ── CUSTOM TRAIN ─────────────────────────────────────────────────
  const trainCustom = async () => {
    if (!brainReady) return;
    await ensureBrain();
    setTrainStatus('training'); setTrainLoss([]);
    const { NeuralNetwork } = window.brain;
    const rows = csvData.trim().split('\n');
    const headers = rows[0].split(',');
    const dataRows = rows.slice(1).map(r => {
      const vals = r.split(',').map(Number);
      const inp = {}, out = {};
      headers.slice(0, -1).forEach((h, i) => inp[h] = vals[i]);
      out[headers[headers.length - 1]] = vals[vals.length - 1];
      return { input: inp, output: out };
    });
    const net = new NeuralNetwork({ hiddenLayers: Array(2).fill(trainHidden), activation: 'sigmoid' });
    const lh = [];
    net.train(dataRows, { iterations: trainEpochs, errorThresh: 0.005, callbackPeriod: Math.max(10, Math.floor(trainEpochs / 30)),
      callback: ({ iterations, error }) => { lh.push({ epoch: iterations, loss: +error.toFixed(5) }); setTrainLoss([...lh]); },
    });
    setTrainStatus('ready');
  };

  // ── BUILD helpers ─────────────────────────────────────────────────
  const addLayer = () => {
    const nl = [...layers]; nl.splice(nl.length - 1, 0, { n: 4, label: `Hidden ${nl.length - 1}` }); setLayers(nl);
  };
  const totalParams = () => layers.slice(1).reduce((s, l, i) => s + l.n * (layers[i].n + 1), 0);

  // ── ACTIVATION CURVE DATA ─────────────────────────────────────────
  const actData = Array.from({ length: 41 }, (_, i) => {
    const x = +(((i - 20) * 0.25).toFixed(2));
    return { x, sigmoid: +ACTS.sigmoid(x).toFixed(3), relu: +ACTS.relu(x).toFixed(3), tanh: +ACTS.tanh(x).toFixed(3), leaky: +ACTS.leaky(x).toFixed(3) };
  });

  return (
    <div className="anim-fadeup" style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
      {/* Tabs */}
      <div style={{ display: 'flex', gap: '3px', background: 'var(--deep)', padding: '4px', borderRadius: '3px', border: '1px solid var(--b0)', overflowX: 'auto' }}>
        {TABS.map(t => (
          <button key={t} onClick={() => setTab(t)} style={{ padding: '6px 14px', border: `1px solid ${tab === t ? ACCENT : 'transparent'}`, background: tab === t ? ACCENT + '18' : 'transparent', color: tab === t ? ACCENT : 'var(--t3)', fontSize: '10px', cursor: 'pointer', borderRadius: '2px', fontFamily: 'JetBrains Mono', whiteSpace: 'nowrap', fontWeight: tab === t ? 700 : 400 }}>{t.toUpperCase()}</button>
        ))}
      </div>

      {!brainReady && (
        <div style={{ padding: '10px 14px', background: 'var(--raised)', border: '1px solid var(--b0)', borderRadius: '4px', fontSize: '11px', color: 'var(--t3)' }}>⟳ Loading Brain.js from CDN…</div>
      )}

      {/* ── OVERVIEW ── */}
      {tab === 'overview' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          <Panel title="BRAIN.JS — IN-BROWSER NEURAL NETWORKS" accent={ACCENT}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
              <div style={{ fontSize: '11px', color: 'var(--t3)', lineHeight: 1.85 }}>
                <strong style={{ color: ACCENT }}>Brain.js</strong> is a pure JavaScript neural network library — no downloads, no server, no WASM. Train feedforward NNs and LSTMs on arbitrary data right in the browser. Unlike Transformers.js (pretrained models), Brain.js lets you define your own architecture and <strong style={{ color: 'var(--green)' }}>train from scratch</strong> on custom data.<br /><br />
                Training 5000 XOR epochs typically takes &lt;2 seconds. LSTM on short sequences: 2–10 seconds. Zero GPU acceleration — pure JS on the main thread — which makes the internals maximally transparent and educational.
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '7px' }}>
                {[
                  ['Library size','~2MB UMD', ACCENT],
                  ['Backend','Pure JavaScript (CPU)','var(--orange)'],
                  ['NeuralNetwork','Feedforward + BPTT','var(--cyan)'],
                  ['Recurrent','LSTM · RNNTimeStep','var(--violet)'],
                  ['Activations','sigmoid, relu, tanh, leaky','var(--gold)'],
                  ['No download','Starts instantly','var(--green)'],
                ].map(([k, v, c]) => (
                  <div key={k} style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 10px', background: 'var(--raised)', borderRadius: '3px' }}>
                    <span style={{ fontSize: '11px', color: 'var(--t3)' }}>{k}</span>
                    <span style={{ fontSize: '11px', color: c, fontFamily: 'JetBrains Mono', fontWeight: 700 }}>{v}</span>
                  </div>
                ))}
              </div>
            </div>
          </Panel>
          <Panel title="QUICK API" accent={ACCENT}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
              {[
                { title: 'NeuralNetwork', color: ACCENT, code: `const net = new brain.NeuralNetwork({
  hiddenLayers: [4, 4],
  activation: 'sigmoid',
  learningRate: 0.3,
});
net.train([
  { input: { a:0, b:0 }, output: { y:0 } },
  { input: { a:1, b:0 }, output: { y:1 } },
], { iterations: 2000 });
const out = net.run({ a:1, b:0 });` },
                { title: 'LSTM (sequences)', color: 'var(--violet)', code: `const net = new brain.recurrent.LSTM();
net.train(['hello world', 'foo bar'], {
  iterations: 1000,
  errorThresh: 0.01,
});
// Generate text from seed string
const out = net.run('hel');
// → 'lo world'` },
              ].map(({ title, color, code }) => (
                <div key={title}>
                  <div style={{ fontSize: '10px', fontWeight: 700, color, marginBottom: '6px', fontFamily: 'JetBrains Mono' }}>{title}</div>
                  <pre style={{ margin: 0, padding: '12px', background: 'var(--deep)', border: '1px solid var(--b0)', borderRadius: '3px', fontSize: '10px', color: 'var(--t2)', fontFamily: 'JetBrains Mono', lineHeight: 1.65, overflowX: 'auto' }}>{code}</pre>
                </div>
              ))}
            </div>
          </Panel>
        </div>
      )}

      {/* ── BUILD ── */}
      {tab === 'build' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          <Panel title="NETWORK ARCHITECT" accent={ACCENT}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
              <div>
                <div style={{ fontSize: '9px', color: 'var(--t3)', marginBottom: '10px', letterSpacing: '.1em' }}>LAYERS</div>
                {layers.map((layer, i) => (
                  <div key={i} style={{ display: 'flex', gap: '8px', alignItems: 'center', marginBottom: '7px', padding: '8px 10px', background: 'var(--raised)', borderRadius: '3px', border: `1px solid ${P[i % P.length]}33` }}>
                    <div style={{ width: '8px', height: '8px', background: P[i % P.length], borderRadius: '50%', flexShrink: 0 }} />
                    <span style={{ fontSize: '11px', color: P[i % P.length], fontFamily: 'JetBrains Mono', fontWeight: 700, minWidth: '72px' }}>{layer.label}</span>
                    <input type="range" min={1} max={16} value={layer.n} onChange={e => setLayers(ls => ls.map((l, j) => j === i ? { ...l, n: +e.target.value } : l))} disabled={i === 0 || i === layers.length - 1} style={{ flex: 1, accentColor: P[i % P.length] }} />
                    <span style={{ fontSize: '13px', fontWeight: 700, color: P[i % P.length], fontFamily: 'JetBrains Mono', minWidth: '22px' }}>{layer.n}</span>
                    {i > 0 && i < layers.length - 1 && (
                      <button onClick={() => setLayers(ls => ls.filter((_, j) => j !== i))} style={{ background: 'transparent', border: '1px solid var(--red)44', color: 'var(--red)', cursor: 'pointer', borderRadius: '2px', width: '22px', height: '22px', fontSize: '12px', flexShrink: 0 }}>×</button>
                    )}
                  </div>
                ))}
                <div style={{ display: 'flex', gap: '8px', marginTop: '12px', flexWrap: 'wrap', alignItems: 'center' }}>
                  <Btn onClick={addLayer} color="var(--green)" style={{ fontSize: '9px', padding: '6px 12px' }}>+ ADD LAYER</Btn>
                  <span style={{ fontSize: '10px', color: 'var(--t3)' }}>activation:</span>
                  {['sigmoid','relu','tanh','leaky'].map(a => (
                    <button key={a} onClick={() => setBuildAct(a)} style={{ padding: '4px 8px', border: `1px solid ${buildAct === a ? ACCENT : 'var(--b0)'}`, background: buildAct === a ? ACCENT + '18' : 'transparent', color: buildAct === a ? ACCENT : 'var(--t3)', fontSize: '9px', cursor: 'pointer', borderRadius: '2px', fontFamily: 'JetBrains Mono' }}>{a}</button>
                  ))}
                </div>
                <div style={{ marginTop: '12px', display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                  <Tag>{layers.length} layers</Tag>
                  <Tag color="var(--cyan)">{layers.map(l => l.n).join('→')}</Tag>
                  <Tag color="var(--gold)">{totalParams()} params</Tag>
                </div>
              </div>
              <div>
                <div style={{ fontSize: '9px', color: 'var(--t3)', marginBottom: '10px', letterSpacing: '.1em' }}>DIAGRAM</div>
                <div style={{ background: 'var(--deep)', padding: '12px', borderRadius: '4px', border: '1px solid var(--b0)', display: 'flex', justifyContent: 'center' }}>
                  <NetDiagram layers={layers} />
                </div>
                <div style={{ fontSize: '10px', color: 'var(--t3)', marginTop: '10px', lineHeight: 1.7 }}>
                  Total params = Σᵢ (nᵢ₋₁ + 1) × nᵢ (the +1 is the bias term per neuron)
                </div>
              </div>
            </div>
          </Panel>
          <Panel title="ACTIVATION FUNCTION COMPARISON" accent={ACCENT}>
            <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '16px' }}>
              <div>
                <div style={{ fontSize: '9px', color: 'var(--t3)', marginBottom: '8px', letterSpacing: '.1em' }}>f(x) CURVES</div>
                <ResponsiveContainer width="100%" height={200}>
                  <LineChart data={actData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="var(--b0)" />
                    <XAxis dataKey="x" stroke="var(--t3)" tick={{ fontSize: 8 }} interval={4} />
                    <YAxis stroke="var(--t3)" tick={{ fontSize: 8 }} domain={[-0.2, 1.2]} />
                    <Tooltip />
                    <Legend wrapperStyle={{ fontSize: '9px' }} />
                    <Line type="monotone" dataKey="sigmoid" stroke={ACCENT} strokeWidth={2} dot={false} />
                    <Line type="monotone" dataKey="relu"    stroke="var(--green)" strokeWidth={2} dot={false} />
                    <Line type="monotone" dataKey="tanh"    stroke="var(--cyan)" strokeWidth={2} dot={false} />
                    <Line type="monotone" dataKey="leaky"   stroke="var(--violet)" strokeWidth={2} dot={false} strokeDasharray="4 2" />
                  </LineChart>
                </ResponsiveContainer>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {[
                  { name:'sigmoid', c:ACCENT, note:'Output layer (0→1). Vanishing gradient in deep nets.' },
                  { name:'relu',    c:'var(--green)', note:'Best for hidden layers. Fast. Dead neuron risk.' },
                  { name:'tanh',    c:'var(--cyan)',  note:'Zero-centered (-1→1). Better than sigmoid for hidden.' },
                  { name:'leaky',   c:'var(--violet)',note:'Fixes dead neurons: 0.01x slope for x<0.' },
                ].map(({ name, c, note }) => (
                  <div key={name} style={{ padding: '8px 10px', background: 'var(--raised)', border: `1px solid ${c}33`, borderRadius: '3px' }}>
                    <div style={{ fontSize: '10px', fontWeight: 700, color: c, fontFamily: 'JetBrains Mono', marginBottom: '3px' }}>{name}</div>
                    <div style={{ fontSize: '9px', color: 'var(--t3)', lineHeight: 1.55 }}>{note}</div>
                  </div>
                ))}
              </div>
            </div>
          </Panel>
        </div>
      )}

      {/* ── TRAIN ── */}
      {tab === 'train' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          <Panel title="CUSTOM CSV TRAINING" accent={ACCENT}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px' }}>
              <div>
                <div style={{ fontSize: '9px', color: 'var(--t3)', marginBottom: '5px', letterSpacing: '.1em' }}>CSV (last column = output)</div>
                <textarea value={csvData} onChange={e => setCsvData(e.target.value)} rows={10} style={{ width: '100%', background: 'var(--deep)', border: '1px solid var(--b1)', color: 'var(--t1)', padding: '10px 12px', fontSize: '11px', fontFamily: 'JetBrains Mono', borderRadius: '3px', outline: 'none', resize: 'vertical', lineHeight: 1.7, boxSizing: 'border-box' }} />
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                <div>
                  <div style={{ fontSize: '10px', color: 'var(--t3)', marginBottom: '5px' }}>Hidden neurons: {trainHidden}</div>
                  <input type="range" min={2} max={16} value={trainHidden} onChange={e => setTrainHidden(+e.target.value)} style={{ width: '100%', accentColor: ACCENT }} />
                </div>
                <div>
                  <div style={{ fontSize: '10px', color: 'var(--t3)', marginBottom: '5px' }}>Epochs: {trainEpochs}</div>
                  <input type="range" min={100} max={2000} step={100} value={trainEpochs} onChange={e => setTrainEpochs(+e.target.value)} style={{ width: '100%', accentColor: ACCENT }} />
                </div>
                <Btn onClick={trainCustom} disabled={!brainReady || trainStatus === 'training'} color={ACCENT}>
                  {trainStatus === 'training' ? 'TRAINING…' : 'TRAIN →'}
                </Btn>
                {trainStatus === 'ready' && <Tag color="var(--green)">✓ Done · final loss: {trainLoss[trainLoss.length-1]?.loss.toFixed(5)}</Tag>}
              </div>
            </div>
          </Panel>
          {trainLoss.length > 1 && (
            <Panel title="LOSS CURVE" accent={ACCENT}>
              <ResponsiveContainer width="100%" height={160}>
                <LineChart data={trainLoss}>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--b0)" />
                  <XAxis dataKey="epoch" stroke="var(--t3)" tick={{ fontSize: 8 }} />
                  <YAxis stroke="var(--t3)" tick={{ fontSize: 8 }} />
                  <Tooltip formatter={v => [v.toFixed(5), 'MSE']} />
                  <Line type="monotone" dataKey="loss" stroke={ACCENT} strokeWidth={2} dot={false} isAnimationActive={false} />
                </LineChart>
              </ResponsiveContainer>
            </Panel>
          )}
        </div>
      )}

      {/* ── XOR ── */}
      {tab === 'xor' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          <Panel title="XOR — THE CLASSIC NN BENCHMARK" accent={ACCENT}>
            <div style={{ fontSize: '11px', color: 'var(--t3)', lineHeight: 1.7, marginBottom: '14px' }}>
              XOR cannot be solved by a linear model. A hidden layer transforms the input space to make it linearly separable. Watch the decision boundary form <strong style={{ color: ACCENT }}>live during training</strong>.
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '10px', marginBottom: '14px' }}>
              <div>
                <div style={{ fontSize: '10px', color: 'var(--t3)', marginBottom: '4px' }}>Hidden neurons: {xorHidden}</div>
                <input type="range" min={2} max={12} value={xorHidden} onChange={e => setXorHidden(+e.target.value)} disabled={xorRunning} style={{ width: '100%', accentColor: ACCENT }} />
              </div>
              <div>
                <div style={{ fontSize: '10px', color: 'var(--t3)', marginBottom: '4px' }}>Learning rate: {xorLR}</div>
                <input type="range" min={0.05} max={2} step={0.05} value={xorLR} onChange={e => setXorLR(+e.target.value)} disabled={xorRunning} style={{ width: '100%', accentColor: ACCENT }} />
              </div>
              <div>
                <div style={{ fontSize: '10px', color: 'var(--t3)', marginBottom: '4px' }}>Activation</div>
                <div style={{ display: 'flex', gap: '4px' }}>
                  {['sigmoid','tanh','relu'].map(a => (
                    <button key={a} onClick={() => setXorAct(a)} disabled={xorRunning} style={{ padding: '4px 8px', border: `1px solid ${xorAct === a ? ACCENT : 'var(--b0)'}`, background: xorAct === a ? ACCENT + '18' : 'transparent', color: xorAct === a ? ACCENT : 'var(--t3)', fontSize: '9px', cursor: 'pointer', borderRadius: '2px', fontFamily: 'JetBrains Mono' }}>{a}</button>
                  ))}
                </div>
              </div>
            </div>
            <div style={{ display: 'flex', gap: '10px', marginBottom: '14px', alignItems: 'center', flexWrap: 'wrap' }}>
              <Btn onClick={startXOR} color={xorRunning ? 'var(--orange)' : ACCENT}>
                {xorRunning ? '⏹ STOP' : '▶ TRAIN (5000 epochs)'}
              </Btn>
              <Tag>{xorEpochs.toLocaleString()} epochs</Tag>
              {xorLoss.length > 0 && <Tag color="var(--green)">loss: {xorLoss[xorLoss.length-1]?.loss.toFixed(5)}</Tag>}
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '200px 1fr', gap: '16px', alignItems: 'start' }}>
              <div>
                <div style={{ fontSize: '9px', color: 'var(--t3)', marginBottom: '6px', letterSpacing: '.1em' }}>DECISION BOUNDARY (live)</div>
                <DecisionBoundary netRef={xorNetRef} xorData={XOR} />
                <div style={{ fontSize: '9px', color: 'var(--t3)', marginTop: '5px' }}>
                  <span style={{ color: '#4ade80' }}>● y=1</span>  <span style={{ color: '#f472b6' }}>● y=0</span>
                </div>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {xorLoss.length > 1 && (
                  <>
                    <div style={{ fontSize: '9px', color: 'var(--t3)', letterSpacing: '.1em' }}>LOSS CURVE</div>
                    <ResponsiveContainer width="100%" height={130}>
                      <LineChart data={xorLoss}>
                        <CartesianGrid strokeDasharray="3 3" stroke="var(--b0)" />
                        <XAxis dataKey="epoch" stroke="var(--t3)" tick={{ fontSize: 8 }} />
                        <YAxis stroke="var(--t3)" tick={{ fontSize: 8 }} />
                        <Tooltip formatter={v => [v.toFixed(5), 'MSE']} />
                        <Line type="monotone" dataKey="loss" stroke={ACCENT} strokeWidth={2} dot={false} isAnimationActive={false} />
                      </LineChart>
                    </ResponsiveContainer>
                  </>
                )}
                {xorPreds.length > 0 && (
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '11px', fontFamily: 'JetBrains Mono' }}>
                    <thead><tr style={{ background: 'var(--deep)' }}>
                      {['Input','Expected','Predicted','✓?'].map(h => <th key={h} style={{ padding: '6px 10px', textAlign: 'left', color: 'var(--t3)', fontSize: '9px', borderBottom: '1px solid var(--b0)' }}>{h}</th>)}
                    </tr></thead>
                    <tbody>
                      {xorPreds.map(({ inp, exp, pred }) => {
                        const ok = (pred > 0.5) === (exp > 0.5);
                        return (
                          <tr key={inp} style={{ borderBottom: '1px solid var(--b0)33' }}>
                            <td style={{ padding: '6px 10px' }}>[{inp}]</td>
                            <td style={{ padding: '6px 10px', color: exp > 0.5 ? 'var(--green)' : 'var(--red)' }}>{exp}</td>
                            <td style={{ padding: '6px 10px', color: pred > 0.5 ? 'var(--green)' : 'var(--red)', fontWeight: 700 }}>{pred}</td>
                            <td style={{ padding: '6px 10px' }}>{ok ? <Tag color="var(--green)">✓</Tag> : <Tag color="var(--red)">✗</Tag>}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
          </Panel>
          {xorSnap && (
            <Panel title="WEIGHT MATRIX (live)" accent={ACCENT}>
              <div style={{ fontSize: '9px', color: 'var(--t3)', marginBottom: '10px' }}>Green = positive, Red = negative. Intensity = magnitude.</div>
              <WeightHeatmap snap={xorSnap} />
            </Panel>
          )}
        </div>
      )}

      {/* ── PATTERNS ── */}
      {tab === 'patterns' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          <Panel title="INPUT→OUTPUT PATTERN RECOGNITION" accent={ACCENT}>
            <div style={{ fontSize: '11px', color: 'var(--t3)', marginBottom: '14px', lineHeight: 1.7 }}>
              Define arbitrary input→output pairs. The network learns to generalize to unseen inputs. Keywords are encoded as character frequency vectors for flexible matching.
            </div>
            <div style={{ marginBottom: '14px' }}>
              <div style={{ fontSize: '9px', color: 'var(--t3)', marginBottom: '8px', letterSpacing: '.1em' }}>PATTERNS</div>
              {patterns.map((p, i) => (
                <div key={i} style={{ display: 'flex', gap: '8px', marginBottom: '6px', alignItems: 'center' }}>
                  <input value={p.input} onChange={e => setPatterns(ps => ps.map((x, j) => j === i ? { ...x, input: e.target.value } : x))} style={{ flex: 2, background: 'var(--deep)', border: '1px solid var(--b1)', color: 'var(--t1)', padding: '7px 10px', fontSize: '11px', fontFamily: 'JetBrains Mono', borderRadius: '3px', outline: 'none' }} placeholder="input keywords" />
                  <span style={{ color: 'var(--t3)' }}>→</span>
                  <input value={p.output} onChange={e => setPatterns(ps => ps.map((x, j) => j === i ? { ...x, output: e.target.value } : x))} style={{ flex: 1, background: 'var(--deep)', border: '1px solid var(--b1)', color: 'var(--t1)', padding: '7px 10px', fontSize: '11px', fontFamily: 'JetBrains Mono', borderRadius: '3px', outline: 'none' }} placeholder="label" />
                  <button onClick={() => setPatterns(ps => ps.filter((_, j) => j !== i))} style={{ background: 'transparent', border: '1px solid var(--red)44', color: 'var(--red)', cursor: 'pointer', borderRadius: '2px', width: '24px', height: '24px', fontSize: '12px' }}>×</button>
                </div>
              ))}
              <Btn onClick={() => setPatterns(ps => [...ps, { input: '', output: '' }])} color="var(--green)" style={{ fontSize: '9px', padding: '5px 10px', marginTop: '6px' }}>+ ADD PATTERN</Btn>
            </div>
            <Btn onClick={trainPatterns} disabled={!brainReady || patStatus === 'training'} color={ACCENT}>
              {patStatus === 'training' ? 'TRAINING…' : '▶ TRAIN ON PATTERNS'}
            </Btn>
          </Panel>
          {patLoss.length > 1 && (
            <Panel title="LOSS" accent={ACCENT}>
              <ResponsiveContainer width="100%" height={130}>
                <LineChart data={patLoss}>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--b0)" />
                  <XAxis dataKey="epoch" stroke="var(--t3)" tick={{ fontSize: 8 }} />
                  <YAxis stroke="var(--t3)" tick={{ fontSize: 8 }} />
                  <Tooltip formatter={v => [v.toFixed(5), 'MSE']} />
                  <Line type="monotone" dataKey="loss" stroke={ACCENT} strokeWidth={2} dot={false} isAnimationActive={false} />
                </LineChart>
              </ResponsiveContainer>
            </Panel>
          )}
          {patStatus === 'ready' && (
            <Panel title="TEST" accent={ACCENT}>
              <div style={{ display: 'flex', gap: '10px', marginBottom: '12px', flexWrap: 'wrap', alignItems: 'center' }}>
                <input value={patQuery} onChange={e => setPatQuery(e.target.value)} placeholder="test input" style={{ flex: 1, minWidth: '200px', background: 'var(--deep)', border: '1px solid var(--b1)', color: 'var(--t1)', padding: '9px 12px', fontSize: '12px', fontFamily: 'JetBrains Mono', borderRadius: '3px', outline: 'none' }} />
                <Btn onClick={runPattern} color={ACCENT}>PREDICT →</Btn>
              </div>
              {patResult && patResult.map(({ label, score }, i) => (
                <div key={label} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '7px 12px', background: 'var(--raised)', borderRadius: '3px', border: i === 0 ? `1px solid ${ACCENT}44` : '1px solid var(--b0)', marginBottom: '5px' }}>
                  <span style={{ fontSize: '12px', color: i === 0 ? ACCENT : 'var(--t2)', fontWeight: i === 0 ? 700 : 400, flex: 1, fontFamily: 'JetBrains Mono' }}>{label}</span>
                  <div style={{ width: '120px', height: '6px', background: 'var(--deep)', borderRadius: '3px' }}>
                    <div style={{ height: '100%', width: `${score * 100}%`, background: P[i % P.length], borderRadius: '3px' }} />
                  </div>
                  <span style={{ fontSize: '11px', color: P[i % P.length], fontFamily: 'JetBrains Mono', fontWeight: 700, minWidth: '44px', textAlign: 'right' }}>{(score * 100).toFixed(1)}%</span>
                </div>
              ))}
            </Panel>
          )}
        </div>
      )}

      {/* ── RNN ── */}
      {tab === 'rnn' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          <Panel title="LSTM — CHARACTER-LEVEL SEQUENCE PREDICTION" accent="var(--violet)">
            <div style={{ fontSize: '11px', color: 'var(--t3)', lineHeight: 1.75, marginBottom: '14px' }}>
              <strong style={{ color: 'var(--violet)' }}>Long Short-Term Memory</strong> networks learn to continue sequences one character at a time. Train on any text, then generate completions from a seed. The LSTM maintains gated cell state for long-range context.
            </div>
            <div style={{ display: 'flex', gap: '8px', marginBottom: '10px', flexWrap: 'wrap' }}>
              {[['Repetitive','hello world how are you today hello world'],['Pattern','abcde abcde abcde abcde abcde abcde'],['Words','the cat sat on the mat the cat ran']].map(([l, t]) => (
                <button key={l} onClick={() => setRnnText(t)} style={{ padding: '5px 12px', border: '1px solid var(--b0)', background: 'var(--raised)', color: 'var(--t3)', fontSize: '9px', cursor: 'pointer', borderRadius: '2px', fontFamily: 'JetBrains Mono' }}>{l}</button>
              ))}
            </div>
            <textarea value={rnnText} onChange={e => setRnnText(e.target.value)} rows={3} style={{ width: '100%', background: 'var(--deep)', border: '1px solid var(--b1)', color: 'var(--t1)', padding: '10px 12px', fontSize: '12px', fontFamily: 'JetBrains Mono', borderRadius: '3px', outline: 'none', resize: 'vertical', lineHeight: 1.7, boxSizing: 'border-box', marginBottom: '10px' }} />
            <Btn onClick={trainRNN} disabled={!brainReady || rnnStatus === 'training'} color="var(--violet)">
              {rnnStatus === 'training' ? 'TRAINING LSTM…' : '▶ TRAIN LSTM (500 iters)'}
            </Btn>
          </Panel>
          {rnnLoss.length > 1 && (
            <Panel title="LSTM TRAINING LOSS" accent="var(--violet)">
              <ResponsiveContainer width="100%" height={130}>
                <LineChart data={rnnLoss}>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--b0)" />
                  <XAxis dataKey="epoch" stroke="var(--t3)" tick={{ fontSize: 8 }} />
                  <YAxis stroke="var(--t3)" tick={{ fontSize: 8 }} />
                  <Tooltip formatter={v => [v.toFixed(5), 'loss']} />
                  <Line type="monotone" dataKey="loss" stroke="var(--violet)" strokeWidth={2} dot={false} isAnimationActive={false} />
                </LineChart>
              </ResponsiveContainer>
            </Panel>
          )}
          {rnnStatus === 'ready' && (
            <Panel title="GENERATE FROM SEED" accent="var(--violet)">
              <div style={{ display: 'flex', gap: '10px', marginBottom: '12px', flexWrap: 'wrap', alignItems: 'center' }}>
                <input value={rnnSeed} onChange={e => setRnnSeed(e.target.value)} placeholder="seed text" style={{ flex: 1, minWidth: '160px', background: 'var(--deep)', border: '1px solid var(--b1)', color: 'var(--t1)', padding: '9px 12px', fontSize: '13px', fontFamily: 'JetBrains Mono', borderRadius: '3px', outline: 'none' }} />
                <Btn onClick={runRNN} color="var(--violet)">GENERATE →</Btn>
              </div>
              {rnnResult && (
                <div style={{ padding: '14px', background: 'var(--deep)', borderRadius: '4px', border: '1px solid var(--violet)22', fontSize: '15px', lineHeight: 1.8, fontFamily: 'JetBrains Mono' }}>
                  <span style={{ color: 'var(--cyan)' }}>{rnnSeed}</span>
                  <span style={{ color: 'var(--violet)' }}>{rnnResult}</span>
                </div>
              )}
            </Panel>
          )}
          <Panel title="LSTM GATES" accent="var(--violet)">
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
              {[
                { gate:'Forget gate', formula:'f_t = σ(W_f·[h_{t-1},x_t] + b_f)', desc:'Decides what fraction of cell state to forget. f=0 clears memory, f=1 preserves it fully. Controls long-range dependencies.', color:'var(--red)' },
                { gate:'Input gate', formula:'i_t = σ(W_i·[h,x]+b_i); C̃ = tanh(W_C·[h,x]+b_C)', desc:'Selects which new information to store. Candidate values C̃ are filtered by gate i before adding to cell state.', color:'var(--green)' },
                { gate:'Cell state highway', formula:'C_t = f_t⊙C_{t-1} + i_t⊙C̃_t', desc:'The "memory highway" — linear operations only, gradients flow back without vanishing. Key to learning long sequences.', color:'var(--cyan)' },
                { gate:'Output gate', formula:'o_t = σ(W_o·[h,x]+b_o); h_t = o_t⊙tanh(C_t)', desc:'Determines the output hidden state. h_t feeds into next time step and into predictions at current step.', color:'var(--violet)' },
              ].map(({ gate, formula, desc, color }) => (
                <div key={gate} style={{ padding: '12px', background: 'var(--raised)', border: `1px solid ${color}33`, borderRadius: '4px' }}>
                  <div style={{ fontSize: '11px', fontWeight: 700, color, marginBottom: '5px' }}>{gate}</div>
                  <div style={{ padding: '4px 7px', background: 'var(--deep)', borderRadius: '2px', fontFamily: 'JetBrains Mono', fontSize: '8px', color: 'var(--code)', marginBottom: '7px' }}>{formula}</div>
                  <div style={{ fontSize: '10px', color: 'var(--t3)', lineHeight: 1.65 }}>{desc}</div>
                </div>
              ))}
            </div>
          </Panel>
        </div>
      )}

      {/* ── VISUALIZE ── */}
      {tab === 'visualize' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          <Panel title="WEIGHT VISUALIZATION" accent={ACCENT}>
            {xorSnap ? (
              <>
                <div style={{ fontSize: '11px', color: 'var(--t3)', marginBottom: '12px', lineHeight: 1.7 }}>
                  Weights from XOR training ({xorEpochs.toLocaleString()} epochs). <strong style={{ color: ACCENT }}>Green = positive, Red = negative.</strong> Intensity = magnitude.
                </div>
                <WeightHeatmap snap={xorSnap} />
                <div style={{ marginTop: '16px' }}>
                  <div style={{ fontSize: '9px', color: 'var(--t3)', marginBottom: '8px', letterSpacing: '.1em' }}>WEIGHT DISTRIBUTION</div>
                  {(()=>{
                    const allW = [...xorSnap.w1.flat(), ...xorSnap.w2];
                    const bins = Array.from({ length: 10 }, (_, i) => {
                      const lo = -2 + i * 0.4, hi = lo + 0.4;
                      return { range: lo.toFixed(1), count: allW.filter(v => v >= lo && v < hi).length };
                    });
                    return (
                      <ResponsiveContainer width="100%" height={120}>
                        <BarChart data={bins}>
                          <CartesianGrid strokeDasharray="3 3" stroke="var(--b0)" />
                          <XAxis dataKey="range" stroke="var(--t3)" tick={{ fontSize: 8 }} />
                          <YAxis stroke="var(--t3)" tick={{ fontSize: 8 }} />
                          <Tooltip />
                          <Bar dataKey="count" radius={[2,2,0,0]}>
                            {bins.map((b, i) => <Cell key={i} fill={+b.range < 0 ? ACCENT : 'var(--green)'} opacity={0.8} />)}
                          </Bar>
                        </BarChart>
                      </ResponsiveContainer>
                    );
                  })()}
                </div>
              </>
            ) : (
              <div style={{ padding: '40px', textAlign: 'center', color: 'var(--t3)', fontSize: '12px' }}>
                Train the XOR network to populate weight visualization.
                <br /><br />
                <Btn onClick={() => setTab('xor')} color={ACCENT}>GO TO XOR TAB →</Btn>
              </div>
            )}
          </Panel>
          <Panel title="ACTIVATION FUNCTIONS (individual)" accent={ACCENT}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
              {['sigmoid','relu','tanh','leaky'].map((act, i) => {
                const data = Array.from({ length: 21 }, (_, j) => { const x = +(((j-10)*0.5).toFixed(1)); return { x, y: +ACTS[act](x).toFixed(3) }; });
                return (
                  <div key={act}>
                    <div style={{ fontSize: '10px', fontWeight: 700, color: P[i], marginBottom: '6px', fontFamily: 'JetBrains Mono' }}>{act}(x)</div>
                    <ResponsiveContainer width="100%" height={100}>
                      <LineChart data={data}>
                        <XAxis dataKey="x" stroke="var(--t3)" tick={{ fontSize: 7 }} interval={3} />
                        <YAxis stroke="var(--t3)" tick={{ fontSize: 7 }} />
                        <Line type="monotone" dataKey="y" stroke={P[i]} strokeWidth={2} dot={false} />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                );
              })}
            </div>
          </Panel>
        </div>
      )}

      {/* ── CONCEPTS ── */}
      {tab === 'concepts' && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
          {[
            { title:'Backpropagation', color:ACCENT, desc:'The core learning algorithm. Computes ∂L/∂w for every weight using the chain rule, propagating error from output back to input. Each step: forward pass → compute loss → backward pass → update weights w ← w − η·∂L/∂w. Brain.js implements vanilla stochastic gradient descent.' },
            { title:'Vanishing Gradient', color:'var(--orange)', desc:'Sigmoid/tanh squash gradients to (0,1). Multiplied across many layers → exponential shrinkage → early layers stop learning. Solution: ReLU (gradient = 1 for x>0), residual connections (ResNets), batch normalization. This is why deep networks prefer ReLU over sigmoid.' },
            { title:'Why XOR needs a hidden layer', color:'var(--cyan)', desc:'XOR is not linearly separable — no straight line separates (0,0),(1,1)=0 from (1,0),(0,1)=1. Single-layer perceptron cannot solve it. The hidden layer learns a nonlinear transformation that makes the problem linearly separable in hidden-layer space. Minsky & Papert proved this limitation in 1969.' },
            { title:'LSTM vs Vanilla RNN', color:'var(--violet)', desc:'Vanilla RNNs suffer vanishing/exploding gradients over long sequences. LSTM adds 3 gates (forget/input/output) and a cell state "highway". Cell state uses only element-wise operations, letting gradients flow back over hundreds of steps. Hochreiter & Schmidhuber 1997.' },
            { title:'Universal Approximation', color:'var(--green)', desc:'Cybenko 1989: a 1-hidden-layer network of sufficient width can approximate any continuous function on a compact domain. Doesn\'t explain how to find weights — that\'s the learning problem. In practice, deeper thinner networks generalize better than single very-wide ones (depth vs width tradeoff).' },
            { title:'Brain.js vs Production ML', color:'var(--gold)', desc:'Brain.js: excellent for teaching, prototyping, <1000 sample problems. For production: TensorFlow.js (WebGL GPU), ONNX Runtime Web (optimized inference), Transformers.js (pretrained SOTA). Brain.js has no GPU support, no autodiff, no batching, no modern optimizers (Adam, SGD momentum). Its strength is extreme simplicity.' },
          ].map(({ title, color, desc }) => (
            <div key={title} style={{ padding: '16px', background: 'var(--raised)', border: `1px solid ${color}33`, borderRadius: '4px' }}>
              <div style={{ color, fontWeight: 700, fontSize: '13px', marginBottom: '8px' }}>{title}</div>
              <div style={{ fontSize: '11px', color: 'var(--t3)', lineHeight: 1.75 }}>{desc}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
